import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '../services/api';

// Tipos alineados con el Backend
export enum UserRole {
  SUPERADMIN = 'superadmin',
  CLIENT_ADMIN = 'client_admin',
  VIEWER = 'viewer',
}

interface User {
  id: string;
  email: string;
  full_name: string;
  role: UserRole;
  organization_id?: string;
  avatar_url?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  activateAccount: (token: string, password: string) => Promise<void>;
}

// --- CORRECCIÓN AQUÍ: Añadimos 'export' para que otros archivos puedan usar el contexto puro ---
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Cargar usuario al inicio si hay token
  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          // Petición al endpoint /users/me que creamos en el backend
          const response = await api.get<User>('/users/me');
          setUser(response.data);
        } catch (error) {
          console.error("Sesión expirada o inválida");
          localStorage.removeItem('token'); // Limpiar token malo
          setUser(null);
        }
      }
      setIsLoading(false);
    };
    initializeAuth();
  }, []);

  const login = async (email: string, password: string) => {
    const params = new URLSearchParams();
    params.append('username', email);
    params.append('password', password);

    try {
      const response = await api.post('/auth/login', params, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      });

      const { access_token } = response.data;
      localStorage.setItem('token', access_token);

      // Configurar header por defecto inmediatamente
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      // Obtener perfil inmediatamente después del login
      const userResponse = await api.get<User>('/users/me');
      setUser(userResponse.data);

    } catch (error) {
      console.error("Error de login", error);
      throw error;
    }
  };

  const activateAccount = async (token: string, password: string) => {
    try {
      await api.post('/auth/activate', { token, password });
    } catch (error) {
      console.error("Error de activación", error);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated: !!user,
      isLoading,
      login,
      logout,
      activateAccount
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Exportamos el hook aquí mismo también por comodidad
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};